import {
    Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Grid, Rating, Stack, Typography, Button
} from '@mui/material';

const dateText = value => value ?
    new Intl.DateTimeFormat('es-MX', {
        dateStyle: 'long',
        timeStyle: 'short',
        timeZone: 'America/Mexico_City'
    }).format(new Date(value))
:
    'No disponible';

const ReviewDetailDialog = ({ onClose, open, review }) => {
    if (!review)
        return null;

    return (
        <Dialog fullWidth maxWidth="md" onClose={onClose} open={open}>
            <DialogTitle>Detalle de la reseña</DialogTitle>
            <DialogContent>
                <Stack
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    spacing={1}
                >
                    <Box>
                        <Typography variant="h6">{review.product.title}</Typography>
                        {review.variant?.title &&
                            <Typography color="text.secondary">
                                {review.variant.title}
                            </Typography>
                        }
                    </Box>
                    <Rating readOnly value={review.rating} />
                </Stack>

                <Divider sx={{ my: 2 }} />
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                    {review.comment}
                </Typography>

                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ my: 2 }}>
                    <Chip label={review.customer.name} />
                    {review.customer.email && <Chip label={review.customer.email} />}
                    {review.verified_purchase &&
                        <Chip color="success" label="Compra verificada" />
                    }
                    {review.order?.number &&
                        <Chip label={`Pedido ${review.order.number}`} />
                    }
                </Stack>

                <Typography color="text.secondary" variant="body2">
                    Publicada: {dateText(review.created_at)}
                </Typography>
                {review.edited_at &&
                    <Typography color="text.secondary" variant="body2">
                        Editada: {dateText(review.edited_at)}
                    </Typography>
                }

                {review.images.length > 0 &&
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {review.images.map(image =>
                            <Grid item md={4} sm={6} xs={12} key={image.id}>
                                <Box
                                    alt={image.alt}
                                    component="img"
                                    loading="lazy"
                                    src={image.url}
                                    sx={{
                                        borderRadius: 1,
                                        height: 220,
                                        objectFit: 'cover',
                                        width: '100%'
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>
                }

                <Divider sx={{ my: 2 }}>Respuesta de Nefrópolis</Divider>
                {review.admin_reply?.text ?
                    <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 2 }}>
                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                            {review.admin_reply.text}
                        </Typography>
                        <Typography color="text.secondary" variant="caption">
                            {review.admin_reply.active ? 'Activa' : 'Retirada'} ·{' '}
                            {dateText(
                                review.admin_reply.updated_at ||
                                review.admin_reply.created_at
                            )}
                        </Typography>
                        {review.edited_after_reply &&
                            <Chip
                                color="warning"
                                label="El cliente editó después de la respuesta"
                                size="small"
                                sx={{ display: 'flex', mt: 1, width: 'fit-content' }}
                            />
                        }
                    </Box>
                :
                    <Typography color="text.secondary">
                        Esta reseña no tiene respuesta.
                    </Typography>
                }

                <Divider sx={{ my: 2 }}>Historial de moderación</Divider>
                {review.moderation_history.length > 0 ?
                    <Stack spacing={1}>
                        {[...review.moderation_history].reverse().map((item, index) =>
                            <Box
                                key={`${item.created_at}-${index}`}
                                sx={{ borderLeft: '3px solid', borderColor: 'divider', pl: 2 }}
                            >
                                <Typography variant="body2">
                                    {item.action === 'hidden' ? 'Ocultada' : 'Restaurada'}
                                    {' por '}{item.admin_name}
                                </Typography>
                                <Typography color="text.secondary" variant="caption">
                                    {dateText(item.created_at)}
                                    {item.reason ? ` · ${item.reason}` : ''}
                                </Typography>
                                {item.note &&
                                    <Typography sx={{ mt: 0.5 }} variant="body2">
                                        {item.note}
                                    </Typography>
                                }
                            </Box>
                        )}
                    </Stack>
                :
                    <Typography color="text.secondary">
                        Sin acciones de moderación.
                    </Typography>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReviewDetailDialog;
